import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Menu, X } from 'lucide-react';

import Chat from 'components/chatapp/Chat';
import Control from 'components/chatapp/Control';
import { useFetchData } from 'hooks/data';
import { TopicResponse } from 'types/topic';
import topicAPI from 'apis/topicAPI';
import { MessageHistory, SessionHistory } from 'types/session';
import LoadingSpinner from 'components/utils/LoadingSpinner';
import { useAuth } from 'hooks/auth';
import { ENABLE_TOPIC } from 'config';


const ChatApp: React.FC = () => {
  const { userId, role } = useAuth();
  const [sessionId, setSessionId] = useState(uuidv4());
  const [sessionName, setSessionName] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [messages, setMessages] = useState<MessageHistory[]>([]);
  const [sessionList, setSessionList] = useState<SessionHistory[]>([]);
  const [blockControl, setBlockControl] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isEnabledTAS, setIsEnabledTAS] = useState(false);
  const [isEnabledTASExample, setIsEnabledTASExample] = useState(false);
  const [isEnabledTSA, setIsEnabledTSA] = useState(false);
  const [isGuardrailEnabled, setIsGuardrailEnabled] = useState(true);

  const { data: topicsData, loading } = useFetchData<TopicResponse>(topicAPI.getTopics, [], ENABLE_TOPIC);

  useEffect(() => {
    if (topicsData && topicsData.topics.length > 0 && selectedTopic === '') {
      setSelectedTopic(topicsData.topics[0].name);
    }
  }, [topicsData, selectedTopic]);

  // Close sidebar when topic or session changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [selectedTopic, sessionId]);

  if (loading || (ENABLE_TOPIC && selectedTopic === '') || userId === '') {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden md:flex justify-center pt-2 w-[314px] bg-BALGray">
          <Control
            userId={userId}
            sessionId={sessionId}
            setSessionId={setSessionId}
            sessionName={sessionName}
            setSessionName={setSessionName}
            topics={topicsData?.topics ?? []}
            selectedTopic={selectedTopic}
            setSelectedTopic={setSelectedTopic}
            messages={messages}
            sessionList={sessionList}
            setSessionList={setSessionList}
            blockControl={blockControl}
            isEnabledTAS={isEnabledTAS}
            setIsEnabledTAS={setIsEnabledTAS}
            isEnabledTASExample={isEnabledTASExample}
            setIsEnabledTASExample={setIsEnabledTASExample}
            isEnabledTSA={isEnabledTSA}
            setIsEnabledTSA={setIsEnabledTSA}
            role={role}
            isEnabledGuardRail={isGuardrailEnabled}
            setIsGuardrailEnabled={setIsGuardrailEnabled}
          />
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 md:hidden z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <div 
          className={`fixed md:hidden top-0 left-0 h-screen w-screen bg-BALGray transform transition-transform duration-300 z-40 overflow-hidden flex flex-col ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-2 flex-shrink-0">
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto p-2 hover:bg-gray-300 rounded-lg flex"
            >
              <X size={24} />
            </button>
          </div>
          <div className="px-2 flex-1 overflow-hidden flex flex-col">
            <Control
              userId={userId}
              sessionId={sessionId}
              setSessionId={setSessionId}
              sessionName={sessionName}
              setSessionName={setSessionName}
              topics={topicsData?.topics ?? []}
              selectedTopic={selectedTopic}
              setSelectedTopic={setSelectedTopic}
              messages={messages}
              sessionList={sessionList}
              setSessionList={setSessionList}
              blockControl={blockControl}
              isMobile={true}
              isEnabledTAS={isEnabledTAS}
              setIsEnabledTAS={setIsEnabledTAS}
              isEnabledTASExample={isEnabledTASExample}
              setIsEnabledTASExample={setIsEnabledTASExample}
              isEnabledTSA={isEnabledTSA}
              setIsEnabledTSA={setIsEnabledTSA}
              role={role}
              isEnabledGuardRail={isGuardrailEnabled}
              setIsGuardrailEnabled={setIsGuardrailEnabled}
            />
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col relative">
          {/* Mobile Header with Hamburger */}
          <div className="md:hidden flex items-center justify-center bg-BALGray px-4 py-3 border-b border-gray-300 relative">
            <button
              onClick={() => setSidebarOpen(true)}
              className="absolute left-4 p-2 hover:bg-gray-300 rounded-lg"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-lg font-semibold text-BADBlue">LUCA</h1>
          </div>

          {/* Chat Component */}
          <div className="flex-1">
            <Chat
              userId={userId}
              userRole={role}
              sessionId={sessionId}
              setSessionId={setSessionId}
              sessionName={sessionName}
              setSessionName={setSessionName}
              topics={topicsData?.topics ?? []}
              selectedTopic={selectedTopic}
              messages={messages}
              setMessages={setMessages}
              sessionList={sessionList}
              setSessionList={setSessionList}
              setBlock={setBlockControl}
              useTAS={isEnabledTAS}
              useTASExample={isEnabledTASExample}
              useTSA={isEnabledTSA}
              isGuardrailEnabled={isGuardrailEnabled}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatApp;
